import { useParams } from 'react-router-dom';

import { useMinistriesWithLeaders, useMinistry, useProjectStatusCounts } from '../hooks/useOrg';
import { useAuth } from '@/app/providers/AuthProvider';
import { NewProjectButton, ProjectTable, useProjects } from '@/features/projects';
import { Breadcrumb, type Crumb } from '@/shared/components/Breadcrumb';
import { EmptyState } from '@/shared/components/EmptyState';
import { LoadingState } from '@/shared/components/LoadingState';
import { PageHeader } from '@/shared/components/PageHeader';
import { StatusTiles } from '@/shared/components/StatusCounts';
import { messages } from '@/shared/constants/messages';
import { can } from '@/shared/constants/roles';
import { toUserMessage } from '@/shared/lib/errors';
import { aggregateStatusCounts } from '@/shared/lib/statusCounts';

/**
 * Panorama do ministério (US-10): o que está parado, em análise ou em execução.
 *
 * ⚠️ **A listagem deixou de ser agrupada por situação.** US-10 pedia o
 * agrupamento, e ele saiu porque quem responde "quanto tem em cada situação"
 * aqui são os `StatusTiles` logo acima — em números, de uma olhada, sem rolar.
 * Agrupar a lista TAMBÉM por situação repetia a mesma resposta ocupando a tela
 * inteira, e custava a única pergunta que os números não respondem: quando cada
 * coisa acontece. A tabela é ordenada por data, e a situação é uma coluna dela.
 *
 * `showMinistry={false}` na listagem: repetir o nome do ministério em cada
 * linha da tela DELE é ruído — a informação já está no título.
 */
export function MinistryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { currentUser } = useAuth();

  const ministryQuery = useMinistry(id);
  const countsQuery = useProjectStatusCounts();
  const projectsQuery = useProjects({ ministryId: id });
  // Reaproveita a consulta que a aba Coordenações já deixou em cache — o nome do
  // líder não sai de um join, e sim da RPC de projeção reduzida (spec §5.4).
  const ministriesWithLeaders = useMinistriesWithLeaders();

  const error = ministryQuery.error ?? countsQuery.error ?? projectsQuery.error;
  if (error) {
    return <EmptyState title="Não foi possível carregar" description={toUserMessage(error)} />;
  }

  const countRows = countsQuery.data;
  if (ministryQuery.isPending || !countRows) {
    return <LoadingState label="Carregando o ministério…" />;
  }

  const ministry = ministryQuery.data;
  if (!ministry) {
    return <EmptyState title="Ministério não encontrado" description={messages.errors.notFound} />;
  }

  const leaderName =
    ministriesWithLeaders.data?.find((item) => item.ministry_id === ministry.id)?.leader_name ??
    null;

  /**
   * A trilha encolhe com o alcance de quem lê, degrau por degrau — ela é a única
   * coisa da tela que a pessoa pressupõe verdadeira sobre a própria navegação
   * (docblock do `Breadcrumb`), e um degrau que aponta para fora do alcance é
   * caminho inventado.
   *
   * "Coordenações" só para quem o tem no menu: para os outros três papéis a aba
   * saiu, e um "voltar" para a aba que sumiu é a pior espécie de link morto — a
   * rota abre, então a pessoa não descobre que se perdeu.
   *
   * O nome da coordenação vira TEXTO para quem não enxerga além do próprio
   * ministério, em vez de sumir: ele é o contexto que responde "onde isto mora",
   * e essa parte segue verdadeira. O que ele não pode ser é link — a
   * `coordinations_select` deixa o líder abrir aquela tela, e o que há lá são os
   * ministérios irmãos com os contadores zerados pela `projects_select`. É
   * exatamente a moldura que mente que esta rodada existe para fechar.
   */
  const roles = currentUser?.roles ?? [];
  const crumbs: Crumb[] = [
    ...(can(roles, 'readOwnBranch') ? [{ label: 'Coordenações', to: '/coordenacoes' }] : []),
    {
      label: ministry.coordination_name,
      ...(can(roles, 'readOwnCoordination')
        ? { to: `/coordenacoes/${ministry.coordination_id}` }
        : {}),
    },
    { label: ministry.name },
  ];

  return (
    <div className="space-y-8">
      <PageHeader
        breadcrumb={<Breadcrumb items={crumbs} />}
        title={ministry.name}
        /* Duas linhas quando há descrição: o que o ministério FAZ e quem
           responde por ele. A liderança fica por último porque é a que muda —
           e é a linha que já existia sozinha aqui. */
        description={
          <>
            {ministry.description ? (
              <span className="block">{ministry.description}</span>
            ) : null}
            <span className="block">
              {leaderName ? `Liderança: ${leaderName}` : 'Sem liderança vigente cadastrada'}
            </span>
          </>
        }
        actions={<NewProjectButton ministryId={ministry.id} />}
      />

      <StatusTiles
        counts={aggregateStatusCounts(countRows, (row) => row.ministry_id === ministry.id)}
      />

      {projectsQuery.isPending ? (
        <LoadingState label="Carregando projetos…" />
      ) : (
        <ProjectTable
          projects={projectsQuery.data ?? []}
          showMinistry={false}
          emptyTitle={messages.empty.projects}
          emptyDescription="Nada registrado por este ministério ainda."
        />
      )}
    </div>
  );
}
